import { i18n } from '@quadralize/i18n'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@quadralize/ui'
import { cn } from '@quadralize/utils'
import { AltArrowDown, CheckCircle, CloseCircle, MenuDots, Pen } from '@solar-icons/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useMobile } from '../../../hooks/use-mobile'

export interface CustomAction<TData> {
  label: string
  icon: React.ComponentType<{ className?: string }>
  onSelect: (item: TData) => void
  variant?: 'default' | 'danger'
  disabled?: boolean
}

interface DataTableRowActionsProps<TData> {
  item: TData
  onEdit?: (item: TData) => void
  isActive?: boolean
  onToggleStatus?: (item: TData) => void
  customActions?: CustomAction<TData>[]
  customActionGroups?: CustomAction<TData>[][]
  mobileBehavior?: 'dropdown' | 'accordion'
  className?: string
  children?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  hideTrigger?: boolean
}

/**
 * Highly flexible and standardized row actions for CRUD lists.
 * - **Edit**: Optional, with standard Pen icon.
 * - **Toggle Status**: Optional, with Tag/Trash icons.
 * - **Custom Actions**: Extensible via `customActions` array or `children`.
 * - **Custom Action Groups**: Use `customActionGroups` for actions with dividers between groups.
 */
export function DataTableRowActions<TData>({
  item,
  onEdit,
  isActive,
  onToggleStatus,
  customActions,
  customActionGroups,
  mobileBehavior = 'dropdown',
  className,
  children,
  onClick,
  onKeyDown,
  isExpanded: controlledIsExpanded,
  onExpandedChange,
  hideTrigger,
}: DataTableRowActionsProps<TData>) {
  const { t } = useTranslation('ui', { i18n })
  const isMobile = useMobile()
  const [internalIsExpanded, setInternalIsExpanded] = React.useState(false)

  const isExpanded = controlledIsExpanded ?? internalIsExpanded
  const setIsExpanded = React.useCallback(
    (value: boolean | ((old: boolean) => boolean)) => {
      const nextValue = typeof value === 'function' ? value(isExpanded) : value
      if (onExpandedChange) {
        onExpandedChange(nextValue)
      } else {
        setInternalIsExpanded(nextValue)
      }
    },
    [isExpanded, onExpandedChange],
  )
  const interactionHandlers = onClick || onKeyDown ? { onClick, onKeyDown } : {}

  const contentId = React.useId()
  const hasDefaultActions = !!onEdit || !!onToggleStatus
  const hasCustomActions = !!customActions?.length
  const hasCustomActionGroups = !!customActionGroups?.length

  // Convert flat customActions to groups for unified rendering
  const actionGroups = customActionGroups || (customActions ? [customActions] : [])
  const shouldUseAccordion = mobileBehavior === 'accordion' && isMobile && !children

  React.useEffect(() => {
    if (!isMobile && isExpanded) {
      setIsExpanded(false)
    }
  }, [isExpanded, isMobile, setIsExpanded])

  if (shouldUseAccordion) {
    const groupedActions: CustomAction<TData>[][] = []

    if (onEdit) {
      groupedActions.push([
        {
          label: t('dataTable.rowActions.edit'),
          icon: Pen,
          onSelect: onEdit,
        },
      ])
    }

    if (onToggleStatus) {
      groupedActions.push([
        {
          label: isActive
            ? t('dataTable.rowActions.deactivate')
            : t('dataTable.rowActions.reactivate'),
          icon: isActive ? CloseCircle : CheckCircle,
          onSelect: onToggleStatus,
          variant: isActive ? 'danger' : 'default',
        },
      ])
    }

    groupedActions.push(...actionGroups)
    const hasAnyAction = groupedActions.length > 0

    if (!hasAnyAction) {
      return null
    }

    return (
      <div
        className={cn(
          hideTrigger
            ? cn(
                'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )
            : 'w-full',
          className,
        )}
        {...interactionHandlers}
      >
        <div className={cn(hideTrigger ? 'min-h-0 ' : 'w-full')}>
          {!hideTrigger && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'h-8 w-full justify-between rounded-lg border border-border-subtle bg-bg-subtle/40 px-2 text-xs',
                'transition-colors hover:border-border-default hover:bg-bg-subtle',
              )}
              onClick={() => setIsExpanded((current) => !current)}
              aria-expanded={isExpanded}
              aria-controls={contentId}
            >
              <span className="inline-flex items-center gap-1.5 font-semibold text-text-primary">
                Ações
              </span>
              <AltArrowDown
                className={cn(
                  'h-4 w-4 text-text-tertiary transition-transform duration-300 ease-out',
                  isExpanded && 'rotate-180',
                )}
                focusable="false"
              />
            </Button>
          )}

          <div
            id={contentId}
            aria-hidden={!isExpanded}
            className={cn(
              'grid overflow-hidden transition-[grid-template-rows,opacity,transform] duration-300 ease-out motion-reduce:transition-none',
              isExpanded
                ? cn(hideTrigger ? 'pt-2' : 'mt-2', 'grid-rows-[1fr] opacity-100 translate-y-0')
                : 'grid-rows-[0fr] opacity-0 -translate-y-1 pointer-events-none',
            )}
          >
            <div className="min-h-0 rounded-lg border border-border-subtle bg-bg-subtle/30 p-1">
              {groupedActions.map((group, groupIndex) => (
                <div
                  key={`accordion-group-${groupIndex.toString()}`}
                  className={cn(
                    'flex flex-col gap-1',
                    groupIndex > 0 && 'mt-1 border-t border-border-subtle pt-1',
                  )}
                >
                  {group.map((action) => (
                    <Button
                      key={action.label}
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={action.disabled}
                      onClick={() => {
                        action.onSelect(item)
                        setIsExpanded(false)
                      }}
                      className={cn(
                        'h-8 w-full justify-start px-2 text-xs font-medium transition-colors',
                        action.variant === 'danger' && 'text-text-danger hover:text-text-danger',
                      )}
                    >
                      <action.icon className="mr-2 h-4 w-4" />
                      {action.label}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex justify-end outline-none', className)} {...interactionHandlers}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 outline-none">
            <MenuDots className="h-4 w-4" focusable="false" />
            <span className="sr-only">Abrir menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          {onEdit && (
            <DropdownMenuItem onSelect={() => setTimeout(() => onEdit(item), 0)}>
              <Pen className="mr-2 h-4 w-4" />
              {t('dataTable.rowActions.edit')}
            </DropdownMenuItem>
          )}

          {onToggleStatus && (
            <>
              {onEdit && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onSelect={() => setTimeout(() => onToggleStatus(item), 0)}
                className={isActive ? 'text-text-danger focus:text-text-danger' : ''}
              >
                {isActive ? (
                  <>
                    <CloseCircle className="mr-2 h-4 w-4" />
                    {t('dataTable.rowActions.deactivate')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {t('dataTable.rowActions.reactivate')}
                  </>
                )}
              </DropdownMenuItem>
            </>
          )}

          {hasDefaultActions && (hasCustomActions || hasCustomActionGroups || children) && (
            <DropdownMenuSeparator />
          )}

          {actionGroups.map((group, groupIndex) => (
            <React.Fragment key={`group-${groupIndex.toString()}`}>
              {group.map((action) => (
                <DropdownMenuItem
                  key={action.label}
                  onSelect={() => action.onSelect(item)}
                  disabled={action.disabled}
                  className={
                    action.variant === 'danger' ? 'text-text-danger focus:text-text-danger' : ''
                  }
                >
                  <action.icon className="mr-2 h-4 w-4" />
                  {action.label}
                </DropdownMenuItem>
              ))}
              {/* Add separator between groups, but not after the last group */}
              {groupIndex < actionGroups.length - 1 && <DropdownMenuSeparator />}
            </React.Fragment>
          ))}

          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
